import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import MagentoAccounts from "./MagentoAccounts";
import MagentoBotKnowledge from "./MagentoBotKnowledge";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import { IconClose1 } from "@velaro/velaro-component-lib";
import SkillControlsPanel from "../SkillControlsPanel";
import ShopperProfileTab from "../shared/ShopperProfileTab";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import {
  SectionCard,
  Divider,
  SecondaryButton,
  GhostButton
} from "@/components/ds";
import { Lightbulb } from "@/components/ds/icons";

interface MagentoConfig {
  id: number;
  siteId: number;
  displayName: string;
  storeUrl: string;
  storeName: string;
  edition: string;
  magentoVersion: string;
  enabled: boolean;
  createdAt: string;
  accessTokenMasked: string;
}

// ─── Capabilities ─────────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Customer lookup",
    icon: "\uD83D\uDECD\uFE0F",
    items: [
      "Find any customer instantly by email address \u2014 name, customer group, website assignment, and account status",
      "Retrieve full customer details including billing/shipping addresses, custom attributes, and group pricing tier",
      "Works in real time during the chat so agents can answer 'what's my account status?' or 'what group am I in?' instantly",
      "magento_search_customer and magento_get_customer \u2014 AI calls both automatically as needed"
    ]
  },
  {
    title: "Order management",
    icon: "\uD83D\uDCE6",
    items: [
      "Look up any order by increment ID \u2014 returns line items, quantities, pricing, shipping address, carrier, and tracking number",
      "Find all recent orders for a customer's email \u2014 returns order IDs, statuses, and totals",
      "AI can answer 'where is my order?' with live data directly from Magento \u2014 no agent needed",
      "Order statuses supported: Pending, Processing, Complete, Closed, Canceled, Holded, On Hold"
    ]
  },
  {
    title: "Product search",
    icon: "\uD83C\uDFF7\uFE0F",
    items: [
      "Search your Magento product catalog by keyword \u2014 returns name, SKU, price, stock status, and description",
      "AI can answer pre-sales questions about availability, configurable options, and pricing from live catalog data",
      "Special price returned when active \u2014 AI can highlight savings in conversation",
      "Product types supported: simple, configurable, grouped, bundle, virtual, downloadable"
    ]
  },
  {
    title: "Cart & abandoned cart recovery",
    icon: "\uD83D\uDED2",
    items: [
      "magento_create_cart: AI builds a cart with selected products and returns a checkout URL \u2014 zero friction for customers",
      "magento_get_abandoned_carts: Find carts abandoned mid-checkout by email \u2014 powers cart recovery workflows",
      "magento_apply_coupon: AI applies a coupon code to the visitor's cart during the conversation to close hesitant buyers",
      "Abandoned cart recovery: Starter Wizard template combines cart lookup + coupon offer to re-engage lost sales"
    ]
  },
  {
    title: "B2B features (Adobe Commerce)",
    icon: "\uD83C\uDFE2",
    items: [
      "magento_get_company: Look up company accounts, credit limits, and hierarchy for B2B customers",
      "magento_get_requisition_list: Retrieve saved requisition lists for quick reorder workflows",
      "magento_get_negotiable_quote: Check status of negotiable quotes \u2014 pending, approved, countered",
      "magento_check_tier_pricing: Look up customer-group-specific pricing for volume/wholesale accounts"
    ]
  },
  {
    title: "AI workflow tools",
    icon: "\uD83E\uDD16",
    items: [
      "29 tools available: search_customers, get_order, search_orders, get_tracking, cancel_order, add_order_comment, get_invoice, get_credit_memos, search_products, check_stock, get_configurable_options, get_tier_pricing, get_categories, get_reviews, get_recommendations, create_cart, get_cart, update_cart, get_shipping_methods, apply_coupon, get_wishlist, get_store_config, get_company, get_requisition_lists, get_negotiable_quotes, customer_insights, create_coupon, get_refunds, create_refund",
      "No workflow configuration required \u2014 the AI decides when to call Magento based on conversation context",
      "Multiple Magento stores supported \u2014 connect different storefronts or brands on the same Velaro site",
      "Field mapping is optional \u2014 the AI works immediately without it; mapping adds agent sidebar and HTTP POST variables"
    ]
  }
];

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  // Customer
  {
    name: "magento_search_customers",
    label: "Search customers",
    description:
      "Finds a Magento customer by email address. Returns name, customer group, and website assignment."
  },
  // Orders
  {
    name: "magento_get_order",
    label: "Get order by ID",
    description:
      "Retrieves a specific order with status, line items, shipping address, and tracking information."
  },
  {
    name: "magento_search_orders",
    label: "Search orders by email",
    description:
      "Looks up recent orders for a customer email. Returns increment IDs, statuses, dates, and totals."
  },
  {
    name: "magento_get_tracking",
    label: "Get shipment tracking",
    description:
      "Returns carrier, tracking number, and a direct tracking URL for a Magento order shipment."
  },
  {
    name: "magento_cancel_order",
    label: "Cancel order",
    description:
      "Cancels a pending or processing order before it ships. Only works on unshipped orders."
  },
  {
    name: "magento_add_order_comment",
    label: "Add order comment",
    description:
      "Adds a status comment to a Magento order —visible in admin order history."
  },
  {
    name: "magento_get_invoice",
    label: "Get invoices",
    description:
      "Retrieves invoice details for an order —invoice number, total, state, and line items."
  },
  {
    name: "magento_get_credit_memos",
    label: "Get credit memos",
    description:
      "Checks refund/credit memo status for an order. Shows refund amount, adjustments, and state."
  },
  // Catalog
  {
    name: "magento_search_products",
    label: "Search products",
    description:
      "Searches the product catalog by keyword. Returns name, SKU, price, stock status, and URL."
  },
  {
    name: "magento_check_stock",
    label: "Check stock level",
    description:
      "Checks real-time inventory for a product. Returns quantity on hand and urgency messaging when stock is low."
  },
  {
    name: "magento_get_configurable_options",
    label: "Get product options",
    description:
      "Returns all configurable options (color, size, material) for a product with per-option price and stock."
  },
  {
    name: "magento_get_tier_pricing",
    label: "Check tier pricing",
    description:
      "Looks up customer-group and tier-specific pricing for volume/wholesale accounts."
  },
  {
    name: "magento_get_categories",
    label: "Browse by category",
    description:
      "Returns the category tree. Helps customers navigate to the right product group."
  },
  {
    name: "magento_get_reviews",
    label: "Get product reviews",
    description:
      "Fetches customer reviews from Magento's native review system. Returns avg star rating and review highlights."
  },
  // Cart & Checkout
  {
    name: "magento_create_cart",
    label: "Create cart / checkout link",
    description:
      "Builds a Magento cart with selected products and returns a checkout URL for the customer."
  },
  {
    name: "magento_get_cart",
    label: "Get cart",
    description:
      "Retrieves the active cart for a customer with items, quantities, and cart total."
  },
  {
    name: "magento_update_cart",
    label: "Update cart",
    description:
      "Changes item quantities or removes items from a cart. Set quantity to 0 to remove."
  },
  {
    name: "magento_get_shipping_methods",
    label: "Shipping methods",
    description:
      "Gets available shipping options and rates for a cart. Helps customers choose shipping."
  },
  {
    name: "magento_apply_coupon",
    label: "Apply coupon code",
    description: "Applies a promo/coupon code to a cart for a discount."
  },
  // Customer engagement
  {
    name: "magento_get_wishlist",
    label: "Get wishlist",
    description:
      "Views items saved to a customer's wishlist. Useful for suggesting purchases."
  },
  {
    name: "magento_get_store_config",
    label: "Store config",
    description:
      "Gets store info —currency, locale, timezone. Helps the bot format prices correctly."
  },
  {
    name: "magento_get_recommendations",
    label: "Product recommendations",
    description:
      "Returns up to 5 products from the same category as a source product. Use for 'what else goes with X?', 'show me similar products', or proactive upselling after showing an item."
  },
  {
    name: "magento_customer_insights",
    label: "Customer intelligence",
    description:
      "Calculate LTV, VIP status (VIP/Loyal/Returning/New), customer group, purchase frequency, and top products from Magento order history. Gives AI an engagement strategy for each customer."
  },
  {
    name: "magento_create_coupon",
    label: "Create discount coupon",
    description:
      "Creates a real-time Magento cart price rule + coupon code (percentage, fixed amount, or free shipping) with configurable usage limit and expiry. Use for cart recovery, VIP rewards, or price-match situations."
  },
  {
    name: "magento_get_refunds",
    label: "Get refunds",
    description:
      "List credit memos (refunds) for an order —amounts, state, and dates. Resolves the increment ID to the internal entity ID automatically."
  },
  {
    name: "magento_create_refund",
    label: "Create refund",
    description:
      "Issues a credit memo (refund) against a Magento order. Supports partial refunds via adjustment_positive. Optionally notifies the customer."
  },
  // B2B (Adobe Commerce only)
  {
    name: "magento_get_company",
    label: "Get company account",
    description:
      "B2B: Looks up company account details, credit limits, and company hierarchy (Adobe Commerce)."
  },
  {
    name: "magento_get_requisition_lists",
    label: "Requisition lists",
    description:
      "B2B: Retrieve saved requisition lists for quick reorder workflows (Adobe Commerce)."
  },
  {
    name: "magento_get_negotiable_quotes",
    label: "Negotiable quotes",
    description:
      "B2B: Check status of negotiable quotes —pending, approved, countered (Adobe Commerce)."
  }
];

const COMING_SOON_TOOLS: {
  name: string;
  label: string;
  description: string;
}[] = [];

// ─── How it all works ─────────────────────────────────────────────────────────

function HowItWorksCard() {
  return (
    <SectionCard
      title="How Magento works in Velaro"
      description="Three steps \u2014 connect once, everything else is automatic."
      className="mb-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Step 1 */}
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-zinc-800 text-white text-xs font-bold flex items-center justify-center shrink-0">
              1
            </span>
            <span className="font-semibold text-zinc-700 text-sm">
              Connect your store
            </span>
          </div>
          <p className="text-xs text-zinc-500 mb-3">
            Enter your Magento store URL and Integration access token.
          </p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-zinc-600">
              <span className="font-mono bg-white border border-zinc-200 px-1.5 py-0.5 rounded text-[10px]">
                Store URL
              </span>
              <span className="text-zinc-400">https://mystore.example.com</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-zinc-600">
              <span className="font-mono bg-white border border-zinc-200 px-1.5 py-0.5 rounded text-[10px]">
                Access Token
              </span>
              <span className="text-zinc-400">
                from System &rarr; Integrations
              </span>
            </div>
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path
                d="M3 2L7 5L3 8"
                stroke="#94a3b8"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>

        {/* Step 2 */}
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
              2
            </span>
            <span className="font-semibold text-green-800 text-sm">
              AI skills go live
            </span>
          </div>
          <p className="text-xs text-green-700 mb-3">
            29 tools activate immediately —zero configuration. The AI calls them
            based on what visitors ask.
          </p>
          <div className="grid grid-cols-2 gap-1">
            {[
              "Customer lookup",
              "Order status",
              "Product search",
              "Cart recovery",
              "Coupon apply",
              "Tracking link",
              "Stock check",
              "Reviews",
              "Options",
              "Category browse",
              "Tier pricing",
              "Checkout link",
              "Cart view",
              "Order notes",
              "Abandoned carts",
              "Company B2B"
            ].map((t) => (
              <div
                key={t}
                className="flex items-center gap-1 text-[10px] text-green-700"
              >
                <svg
                  viewBox="0 0 8 8"
                  width="8"
                  height="8"
                  fill="none"
                  className="shrink-0"
                >
                  <path
                    d="M1 4L3 6L7 2"
                    stroke="#16a34a"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                {t}
              </div>
            ))}
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path
                d="M3 2L7 5L3 8"
                stroke="#94a3b8"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>

        {/* Step 3 */}
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-(--ds-primary) text-white text-xs font-bold flex items-center justify-center shrink-0">
              3
            </span>
            <span className="font-semibold text-blue-800 text-sm">
              Data surfaces everywhere
            </span>
          </div>
          <p className="text-xs text-blue-700 mb-3">
            Data appears in 3 places automatically \u2014 all without any
            configuration.
          </p>
          <div className="space-y-2">
            <div className="rounded p-2 border border-blue-100 bg-white/80">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">
                Agent sidebar
              </div>
              <div className="text-[10px] text-blue-600 mt-0.5">
                Magento tab &rarr; Customer / Orders / Products panels with full
                data
              </div>
            </div>
            <div className="rounded p-2 border border-blue-100 bg-white/80">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">
                Contact tab strip
              </div>
              <div className="text-[10px] text-blue-600 mt-0.5">
                Customer Group, Lifetime Value, and Order Count at a glance
              </div>
            </div>
            <div className="rounded p-2 border border-blue-100 bg-white/80">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">
                HTTP POST variables
              </div>
              <div className="text-[10px] text-blue-600 mt-0.5">
                Field Reference tab &rarr; copy variable names to push data to
                your CRM
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── Capabilities panel ───────────────────────────────────────────────────────

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));

  function toggleSection(i: number) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  return (
    <SectionCard className="mb-4 !p-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-zinc-50 rounded-2xl"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-zinc-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">
            {CAPABILITIES.length} feature areas
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-zinc-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>

      {open && (
        <div className="px-6 pb-6">
          <Divider />
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
            <strong>AI tools work without any configuration.</strong> Connect
            your Magento store (URL + Access Token) and the AI can immediately
            call customer, order, and product lookup tools during any
            conversation. No JSON, no field mapping, no webhook setup required
            \u2014 the AI orchestrates tool calls automatically.
          </div>

          <div className="space-y-2">
            {CAPABILITIES.map((cap, i) => (
              <div
                key={i}
                className="rounded-lg border border-zinc-100 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleSection(i)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50"
                  aria-expanded={openSections.has(i)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-base">{cap.icon}</span>
                    <span className="text-sm font-medium text-zinc-700">
                      {cap.title}
                    </span>
                  </div>
                  <svg
                    className={`w-3.5 h-3.5 text-zinc-400 transition-transform shrink-0 ${openSections.has(i) ? "rotate-180" : ""}`}
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                  </svg>
                </button>
                {openSections.has(i) && (
                  <div className="px-4 pb-3 space-y-1.5 border-t border-zinc-50">
                    {cap.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2 pt-1.5">
                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 bg-green-500" />
                        <span className="text-xs text-zinc-600">{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-4 text-xs text-blue-800">
            <div className="font-semibold mb-1">How orchestration works</div>
            <p className="mb-2">
              The Magento AI node automatically queries your store as part of AI
              conversations. Use the{" "}
              <strong>E-commerce Specialist (Orchestration)</strong> template to
              create a dedicated Magento AI that a main routing AI can hand off
              to when it detects order or product intent.
            </p>
            <p>
              Field mapping and HTTP POST nodes are optional extras \u2014
              they're useful for pushing data to your CRM, ticketing system, or
              email platform after the AI resolves the conversation.
            </p>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// ─── Data Mapping ─────────────────────────────────────────────────────────────

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-zinc-900 text-zinc-100 rounded p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">
      {children}
    </pre>
  );
}

const MAGENTO_FIELD_REFERENCE = [
  {
    scope: "Customer",
    scopeKey: "customer",
    fields: [
      {
        field: "id",
        label: "Customer ID",
        search: true,
        show: true,
        notes: "Magento internal customer ID"
      },
      {
        field: "email",
        label: "Email",
        search: true,
        show: true,
        notes: "Primary identifier for lookup"
      },
      {
        field: "firstname",
        label: "First Name",
        search: true,
        show: true,
        notes: ""
      },
      {
        field: "lastname",
        label: "Last Name",
        search: true,
        show: true,
        notes: ""
      },
      {
        field: "group_id",
        label: "Customer Group",
        search: false,
        show: true,
        notes: "Group ID for pricing tier"
      },
      {
        field: "created_at",
        label: "Created Date",
        search: false,
        show: true,
        notes: "Account creation date"
      },
      {
        field: "website_id",
        label: "Website ID",
        search: false,
        show: true,
        notes: "Multi-store assignment"
      },
      {
        field: "store_id",
        label: "Store View",
        search: false,
        show: false,
        notes: "Store view assignment"
      },
      {
        field: "default_billing",
        label: "Billing Address ID",
        search: false,
        show: true,
        notes: "Default billing address"
      },
      {
        field: "default_shipping",
        label: "Shipping Address ID",
        search: false,
        show: true,
        notes: "Default shipping address"
      }
    ]
  },
  {
    scope: "Order",
    scopeKey: "order",
    fields: [
      {
        field: "entity_id",
        label: "Order ID",
        search: false,
        show: true,
        notes: "Internal order ID"
      },
      {
        field: "increment_id",
        label: "Order Number",
        search: true,
        show: true,
        notes: "Customer-facing order # (e.g. 000000123)"
      },
      {
        field: "status",
        label: "Status",
        search: false,
        show: true,
        notes: "processing / complete / closed / canceled"
      },
      {
        field: "state",
        label: "State",
        search: false,
        show: true,
        notes: "new / processing / complete / closed"
      },
      {
        field: "created_at",
        label: "Order Date",
        search: false,
        show: true,
        notes: "ISO date string"
      },
      {
        field: "grand_total",
        label: "Grand Total",
        search: false,
        show: true,
        notes: "Total including tax and shipping"
      },
      {
        field: "subtotal",
        label: "Subtotal",
        search: false,
        show: true,
        notes: "Before tax and shipping"
      },
      {
        field: "total_item_count",
        label: "Item Count",
        search: false,
        show: true,
        notes: "Total number of line items"
      },
      {
        field: "customer_email",
        label: "Customer Email",
        search: true,
        show: true,
        notes: ""
      },
      {
        field: "order_currency_code",
        label: "Currency",
        search: false,
        show: false,
        notes: "ISO currency code"
      },
      {
        field: "shipping_description",
        label: "Shipping Method",
        search: false,
        show: true,
        notes: "e.g. Flat Rate - Fixed"
      }
    ]
  },
  {
    scope: "Product",
    scopeKey: "product",
    fields: [
      {
        field: "id",
        label: "Product ID",
        search: false,
        show: true,
        notes: "Magento product ID"
      },
      {
        field: "sku",
        label: "SKU",
        search: true,
        show: true,
        notes: "Stock keeping unit"
      },
      {
        field: "name",
        label: "Product Name",
        search: true,
        show: true,
        notes: "Full product title"
      },
      {
        field: "price",
        label: "Price",
        search: false,
        show: true,
        notes: "Base price excluding tax"
      },
      {
        field: "special_price",
        label: "Special Price",
        search: false,
        show: true,
        notes: "Discounted price if active"
      },
      {
        field: "status",
        label: "Status",
        search: false,
        show: true,
        notes: "1=enabled, 2=disabled"
      },
      {
        field: "visibility",
        label: "Visibility",
        search: false,
        show: false,
        notes: "1=not visible, 2=catalog, 3=search, 4=both"
      },
      {
        field: "type_id",
        label: "Product Type",
        search: false,
        show: true,
        notes: "simple / configurable / grouped / bundle"
      },
      {
        field: "weight",
        label: "Weight",
        search: false,
        show: false,
        notes: "Product weight"
      }
    ]
  }
];

function CopyVariableButton({ variable }: { variable: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(`{${variable}}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* graceful */
    }
  }

  return (
    <GhostButton
      onClick={copy}
      title={`Copy variable: {${variable}}`}
      className={`!text-[10px] !px-1.5 !py-0.5 !rounded border font-mono ${
        copied
          ? "!border-green-300 !bg-green-50 !text-green-700"
          : "!border-zinc-200 !bg-zinc-50 !text-zinc-500 hover:!border-blue-300 hover:!text-blue-700 hover:!bg-blue-50"
      }`}
      aria-label={`Copy variable {${variable}}`}
    >
      {copied ? "\u2713" : `{${variable}}`}
    </GhostButton>
  );
}

function MagentoDataMapping() {
  const [activeScope, setActiveScope] = useState("Customer");
  const scopeData =
    MAGENTO_FIELD_REFERENCE.find((s) => s.scope === activeScope) ??
    MAGENTO_FIELD_REFERENCE[0];

  return (
    <div>
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 mb-4">
        <div className="font-semibold text-blue-900 text-sm mb-1">
          What this is
        </div>
        <p className="text-xs text-blue-800 leading-relaxed">
          A reference of every standard Magento field name and the variable
          syntax you can use in workflow HTTP POST nodes to push data to other
          systems (e.g. your CRM, ticketing, or email platform).
        </p>
        <div className="mt-3 grid grid-cols-1 gap-2 text-xs">
          <div className="flex items-start gap-2 text-blue-800">
            <span className="text-base leading-none">{"\u2705"}</span>
            <span>
              <strong>Agent sidebar</strong> \u2014 automatically shows
              customer, order, and product data during every live chat.
            </span>
          </div>
          <div className="flex items-start gap-2 text-blue-800">
            <span className="text-base leading-none">{"\u2705"}</span>
            <span>
              <strong>At-a-glance strip</strong> \u2014 the main Contact tab
              shows customer group, LTV, and order count.
            </span>
          </div>
          <div className="flex items-start gap-2 text-blue-800">
            <span className="text-base leading-none">{"\uD83D\uDCCB"}</span>
            <span>
              <strong>HTTP POST variables</strong> \u2014 copy any variable
              below and paste it into a workflow HTTP POST node body.
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        {MAGENTO_FIELD_REFERENCE.map((s) => (
          <button
            key={s.scope}
            type="button"
            onClick={() => setActiveScope(s.scope)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeScope === s.scope
                ? "bg-zinc-800 text-white"
                : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
            }`}
          >
            {s.scope}
          </button>
        ))}
      </div>

      <div className="rounded border border-zinc-200 p-3 mb-4 text-xs text-zinc-600">
        <div className="flex items-center justify-between mb-1.5">
          <strong className="text-zinc-700">
            Using these in a workflow HTTP POST node
          </strong>
          <span className="text-zinc-400">
            Click any variable in the table to copy
          </span>
        </div>
        <CodeBlock>{`// Paste into HTTP POST node body \u2192 sends Magento data to your CRM
{
  "customer_email":  "{magento.customer.email}",
  "customer_name":   "{magento.customer.firstname} {magento.customer.lastname}",
  "customer_group":  "{magento.customer.group_id}",
  "order_number":    "{magento.order.increment_id}",
  "order_status":    "{magento.order.status}",
  "order_total":     "{magento.order.grand_total}"
}`}</CodeBlock>
      </div>

      <div className="rounded-xl border border-zinc-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-zinc-800 text-white text-xs font-semibold flex items-center gap-2">
          <span>{scopeData.scope} fields</span>
          <span className="text-zinc-400 font-normal">
            \u2014 all of these are available in every connected store
          </span>
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Magento field
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Friendly name
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Variable to copy
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {scopeData.fields.map((f, i) => (
                <tr
                  key={i}
                  className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}
                >
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-700">
                    {f.field}
                  </td>
                  <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                  <td className="px-3 py-2">
                    <CopyVariableButton
                      variable={`magento.${scopeData.scopeKey}.${f.field}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-zinc-400 italic">
                    {f.notes || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-zinc-400 mt-3">
        These are the standard Magento 2 REST API field names. They are
        identical across every Magento store. The AI, agent sidebar, and
        at-a-glance strip all use these fields automatically.
      </p>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Magento() {
  const api = useApi();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<MagentoConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const { enableEcommerceSync } = useAdminFeatures();
  const [activeTab, setActiveTab] = useState<
    | "overview"
    | "config"
    | "bot-knowledge"
    | "skills"
    | "mapping"
    | "shoppers"
    | "log"
  >("overview");
  const [showWizard, setShowWizard] = useState(false);

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("Magento/Configs");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const hasActiveConfig = configs.some((c) => c.enabled);

  const tabsJSX = (
    <>
      <IntegrationTab
        active={activeTab === "overview"}
        onClick={() => setActiveTab("overview")}
      >
        Overview
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "config"}
        onClick={() => setActiveTab("config")}
      >
        Configuration
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "bot-knowledge"}
        onClick={() => setActiveTab("bot-knowledge")}
      >
        Bot Knowledge
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "skills"}
        onClick={() => setActiveTab("skills")}
      >
        AI Skills
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "mapping"}
        onClick={() => setActiveTab("mapping")}
      >
        Field Reference
      </IntegrationTab>
      {enableEcommerceSync && (
        <IntegrationTab
          active={activeTab === "shoppers"}
          onClick={() => setActiveTab("shoppers")}
        >
          Shoppers
        </IntegrationTab>
      )}
      <IntegrationTab
        active={activeTab === "log"}
        onClick={() => setActiveTab("log")}
      >
        Activity Log
      </IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="Magento" tabs={tabsJSX}>
      {showWizard && (
        <StarterWorkflowWizard
          integration="magento"
          magentoConfigId={configs.find((c) => c.enabled)?.id}
          onClose={() => setShowWizard(false)}
        />
      )}

      {activeTab === "overview" && (
        <div>
          <HowItWorksCard />
          <CapabilitiesPanel />
        </div>
      )}

      {activeTab === "config" && (
        <div>
          {hasActiveConfig && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-blue-800 mb-0.5">
                    Deploy the chat widget to your store
                  </div>
                  <div className="text-xs text-blue-600">
                    Your Magento integration is connected — grab your embed code
                    from Deployments to go live.
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to="/chat_window"
                    className="text-xs px-3 py-1.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-100 transition"
                  >
                    Chat Window
                  </Link>
                  <Link
                    to="/deployment"
                    className="text-xs px-3 py-1.5 rounded-lg bg-(--ds-primary) hover:opacity-90 text-white transition"
                  >
                    Get Embed Code →
                  </Link>
                </div>
              </div>
              <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
                <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  <strong>Tip:</strong> You can also create a{" "}
                  <strong>Knowledge Base Q&A</strong> page widget from{" "}
                  <Link
                    to="/deployment"
                    className="underline hover:text-blue-900"
                  >
                    Deployments → Page Widgets
                  </Link>{" "}
                  for a lightweight, AI-only Q&A experience — no live agent
                  needed.
                </span>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="mb-6">
              <MagentoAccounts
                configs={configs}
                onDelete={(id) =>
                  setConfigs((prev) => prev.filter((c) => c.id !== id))
                }
                onRefresh={fetchConfigs}
              />
            </div>
          )}
        </div>
      )}

      {activeTab === "bot-knowledge" && (
        <MagentoBotKnowledge
          storeUrl={configs.find((c) => c.enabled)?.storeUrl}
        />
      )}

      {activeTab === "skills" && configs.length > 0 && (
        <SectionCard
          title="AI Tools"
          description="The AI calls these automatically based on what visitors ask — no configuration required."
          action={
            hasActiveConfig ? (
              <LaunchWizardButton onClick={() => setShowWizard(true)} />
            ) : undefined
          }
          className="mb-4"
        >
          <div className="mt-2">
            <SkillControlsPanel integration="magento" skills={AI_TOOLS} />
          </div>
        </SectionCard>
      )}

      {activeTab === "mapping" && (
        <SectionCard
          title="Data Mapping"
          description="Configure which Magento fields appear in the agent sidebar and become available as HTTP POST variables. The AI works without this — mapping is optional and additive."
        >
          <MagentoDataMapping />
        </SectionCard>
      )}

      {activeTab === "shoppers" && enableEcommerceSync && (
        <SectionCard
          title="Shopper Profiles — Magento"
          description="All customers synced from your Magento store. Data updates hourly."
        >
          <ShopperProfileTab platform="Magento" />
        </SectionCard>
      )}

      {activeTab === "log" && (
        <SectionCard title="Activity Log">
          <IntegrationActivityLog integrationSlug="Magento" />
        </SectionCard>
      )}
    </StickyRouteHeader>
  );
}

export function BackToIntegrationLandingMagento() {
  const navigate = useNavigate();
  return (
    <div
      className="flex cursor-pointer"
      onClick={() => navigate("/integrations")}
    >
      <IconClose1 className="h-6 w-6" />
    </div>
  );
}
